import useAppMethods from '../../hooks/utility/useAppMethods';
import { useLocationData } from './provider/LocationDataProvider';
import { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import useOrderSlots from '../../hooks/availability/useOrderSlots';
import { useTranslation } from 'react-i18next';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';
import useCompanyBusinessTime from '../../hooks/availability/company-business-time/useCompanyBusinessTime';

interface DayItem {
  label: string;
  dateLabel: string;
  date: Moment;
  active?: boolean;
}

interface Props {
  company: CompanyDetailsInterface;
}

const useScheduledOrderStuff = ({ company }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:useScheduledOrderStuff' });
  const { toggleOrderMethodModal } = useAppMethods();
  const { setScheduledFor, orderMethod } = useLocationData();
  const [orderSlot, setOrderSlot] = useState<string>(null);
  const [now] = useState(() => moment());
  const [date, setDate] = useState<Moment>(null);
  const [more, setMore] = useState<boolean>(false);
  const { orderSlots, isLoading: isOrderSlotsLoading } = useOrderSlots({
    orderMethod,
    date,
    company,
  });
  const { maxPreOrderDays, isHoliday, isBusinessDay } = useCompanyBusinessTime({
    orderMethod,
    company,
  });

  const CUSTOM_CALENDAR = {
    sameDay: `[${t('today')}]`,
    nextDay: `[${t('tomorrow')}]`,
    nextWeek: 'ddd',
    lastDay: 'ddd',
    lastWeek: 'ddd',
    sameElse: 'ddd',
  } as const;

  const onBackClick = () => {
    toggleOrderMethodModal({ openModal: true });
  };

  const days = useMemo(() => {
    if (isOrderSlotsLoading) {
      return [];
    }

    const result: DayItem[] = [];

    for (let i = 0; i <= maxPreOrderDays; i++) {
      let day = now.clone().add(i, 'days');

      if (!day.isSame(now, 'days')) {
        day = day.startOf('day');
      }

      if (isHoliday(day.toISOString())) {
        continue;
      }

      if (!isBusinessDay(day.toISOString())) {
        continue;
      }

      const label = day.calendar(CUSTOM_CALENDAR);
      const active = date && day.isSame(date, 'days');

      result.push({
        label,
        dateLabel: day.format('MMM DD'),
        date: day,
        active,
      });
    }

    if (!result.length || result.length < 2) {
      return result;
    }

    if (more) {
      return result;
    }

    if (!date) {
      result.length = 2;

      return result;
    }

    const activeDayIndex = result.findIndex((item) => item.active);

    if (activeDayIndex < 2) {
      result.length = 2;

      return result;
    }

    const startIndex = activeDayIndex % 2 === 0 ? activeDayIndex : activeDayIndex - 1;

    return result.slice(startIndex, startIndex + 2);
  }, [maxPreOrderDays, isHoliday, more, date, isOrderSlotsLoading, company]);

  const onDayClick = (date: Moment) => {
    setOrderSlot(null);
    setDate(date);
  };

  const onOrderSlotClick = (slot: string) => {
    setOrderSlot(slot);
  };

  const toggleMore = () => setMore((prev) => !prev);
  const onScheduleOrderClick = () => {
    setScheduledFor(orderSlot);
    toggleOrderMethodModal({
      openModal: true,
    });
  };

  const hasOrderSlots = date && !!orderSlots?.length;
  const isScheduleOrderDisabled = isOrderSlotsLoading || !date || !orderSlot;

  useEffect(() => {
    if (!days?.length) {
      return;
    }

    if (date && !isHoliday(date.toISOString()) && isBusinessDay(date.toISOString())) {
      return;
    }

    setDate(days[0].date);
  }, [days]);

  return {
    onBackClick,
    days,
    orderSlots,
    hasOrderSlots,
    more,
    isScheduleOrderDisabled,
    onDayClick,
    toggleMore,
    isLoading: isOrderSlotsLoading,
    onScheduleOrderClick,
    onOrderSlotClick,
    orderSlot,
  };
};

export default useScheduledOrderStuff;
